/**
 * Subscription Settings Tab
 *
 * Organized around the operator's mental model of the free-user paywall journey:
 *   1. Mode selection (smart guidance vs legacy quota) — mutually exclusive cards
 *   2. Smart mode: soft guidance thresholds → hard limits, with a live journey preview
 *   3. Legacy mode: flat message quotas
 *   4. General + collapsed advanced (feature gating) sections
 *
 * Note: trial settings (Apple-controlled) and client-ignored paywall fields
 * (delay_seconds, dismissable, soft_wall) are intentionally not exposed.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, AlertTriangle, ChevronDown, ChevronRight, Info, Save, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { toast } from 'sonner';
import { useI18n } from '@/i18n';
import { baseApiClient } from '@/lib/api/client';

// Types
interface SubscriptionConfig {
  trial: { enabled: boolean; days: number };
  paywall: { show_on_launch: boolean; delay_seconds: number; dismissable: boolean; soft_wall: boolean };
  features: { free: string[]; pro: string[]; business: string[] };
  quota: {
    free_messages_total: number;
    free_messages_daily: number;
    smart_paywall_enabled: boolean;
    free_deep_daily: number;
    fallback_daily_messages: number;
    hard_messages_daily: number;
    hard_deep_daily: number;
  };
  products: Record<string, { id: string; name: string }>;
}

// API functions
async function fetchSubscriptionConfig(): Promise<SubscriptionConfig> {
  const { data } = await baseApiClient.get<SubscriptionConfig>('/v1/subscription/config');
  return data;
}

async function updateSubscriptionConfig(payload: Record<string, unknown>): Promise<SubscriptionConfig> {
  const { data } = await baseApiClient.put<SubscriptionConfig>('/v1/subscription/admin/config', payload);
  return data;
}

// Form state — only fields that actually take effect
interface FormState {
  paywall_show_on_launch: boolean;
  smart_paywall_enabled: boolean;
  free_deep_daily: number;
  fallback_daily_messages: number;
  hard_messages_daily: number;
  hard_deep_daily: number;
  quota_free_messages_total: number;
  quota_free_messages_daily: number;
  features_free: string;
  features_pro: string;
}

function formStateFromConfig(data: SubscriptionConfig): FormState {
  return {
    paywall_show_on_launch: data.paywall.show_on_launch,
    smart_paywall_enabled: data.quota?.smart_paywall_enabled ?? false,
    free_deep_daily: data.quota?.free_deep_daily ?? 5,
    fallback_daily_messages: data.quota?.fallback_daily_messages ?? 8,
    hard_messages_daily: data.quota?.hard_messages_daily ?? 24,
    hard_deep_daily: data.quota?.hard_deep_daily ?? 15,
    quota_free_messages_total: data.quota?.free_messages_total ?? 50,
    quota_free_messages_daily: data.quota?.free_messages_daily ?? 10,
    features_free: data.features.free.join(', '),
    features_pro: data.features.pro.join(', '),
  };
}

interface NumberFieldProps {
  label: string;
  help?: string;
  value: number;
  min?: number;
  onChange: (value: number) => void;
}

function NumberField({ label, help, value, min = 0, onChange }: NumberFieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type="number"
        min={min}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value) || 0)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
      />
      {help && <p className="mt-1 text-xs text-gray-500">{help}</p>}
    </div>
  );
}

export function SubscriptionSettingsTab() {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['subscription-config'],
    queryFn: fetchSubscriptionConfig,
  });

  const [formState, setFormState] = useState<FormState | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Initialize form state when data loads
  if (data && !formState) {
    setFormState(formStateFromConfig(data));
  }

  const mutation = useMutation({
    mutationFn: updateSubscriptionConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-config'] });
      toast.success(t('subscriptionPage.settings.savedSuccess'));
      setHasChanges(false);
    },
    onError: (err: Error) => {
      toast.error(`${t('subscriptionPage.settings.saveFailed')}: ${err.message}`);
    },
  });

  const handleChange = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    if (!formState) return;
    setFormState({ ...formState, [key]: value });
    setHasChanges(true);
  };

  const handleSave = () => {
    if (!formState) return;

    mutation.mutate({
      paywall_show_on_launch: formState.paywall_show_on_launch,
      smart_paywall_enabled: formState.smart_paywall_enabled,
      free_deep_daily: formState.free_deep_daily,
      fallback_daily_messages: formState.fallback_daily_messages,
      hard_messages_daily: formState.hard_messages_daily,
      hard_deep_daily: formState.hard_deep_daily,
      quota_free_messages_total: formState.quota_free_messages_total,
      quota_free_messages_daily: formState.quota_free_messages_daily,
      features_free: formState.features_free.split(',').map(s => s.trim()).filter(Boolean),
      features_pro: formState.features_pro.split(',').map(s => s.trim()).filter(Boolean),
    });
  };

  const handleReset = () => {
    if (!data) return;
    setFormState(formStateFromConfig(data));
    setHasChanges(false);
  };

  const hardBelowGuide =
    formState != null &&
    ((formState.hard_messages_daily > 0 &&
      formState.fallback_daily_messages > 0 &&
      formState.hard_messages_daily < formState.fallback_daily_messages) ||
      (formState.hard_deep_daily > 0 && formState.hard_deep_daily < formState.free_deep_daily));

  const journeySteps = formState
    ? [
        { value: formState.free_deep_daily, label: t('subscriptionPage.settings.journeyDeepGuide'), soft: true },
        {
          value: formState.fallback_daily_messages,
          label: t('subscriptionPage.settings.journeyFallbackGuide'),
          soft: true,
        },
        { value: formState.hard_deep_daily, label: t('subscriptionPage.settings.journeyDeepBlock'), soft: false },
        { value: formState.hard_messages_daily, label: t('subscriptionPage.settings.journeyMsgBlock'), soft: false },
      ]
        .filter((s) => s.value > 0)
        .sort((a, b) => a.value - b.value)
    : [];

  return (
    <div className="max-w-4xl">
      {/* Action Buttons */}
      <div className="flex justify-end gap-2 mb-6">
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          {t('subscriptionPage.settings.refresh')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!hasChanges || mutation.isPending}
        >
          <Save className="w-4 h-4 mr-2" />
          {mutation.isPending ? t('subscriptionPage.settings.saving') : t('subscriptionPage.settings.saveChanges')}
        </Button>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div>
              <h3 className="text-red-800 font-semibold">{t('subscriptionPage.settings.errorLoading')}</h3>
              <p className="text-red-600 text-sm">{(error as Error).message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      )}

      {/* Form */}
      {formState && (
        <div className="space-y-6">
          {/* Mode Selection */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('subscriptionPage.settings.paywallModeTitle')}</h2>
            <div className="grid grid-cols-2 gap-4">
              {[
                {
                  enabled: true,
                  title: t('subscriptionPage.settings.paywallModeSmart'),
                  desc: t('subscriptionPage.settings.paywallModeSmartDesc'),
                },
                {
                  enabled: false,
                  title: t('subscriptionPage.settings.paywallModeLegacy'),
                  desc: t('subscriptionPage.settings.paywallModeLegacyDesc'),
                },
              ].map((mode) => {
                const selected = formState.smart_paywall_enabled === mode.enabled;
                return (
                  <button
                    key={String(mode.enabled)}
                    type="button"
                    onClick={() => handleChange('smart_paywall_enabled', mode.enabled)}
                    className={`text-left p-4 rounded-lg border-2 transition-colors ${
                      selected
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                          selected ? 'border-primary-600 bg-primary-600' : 'border-gray-300'
                        }`}
                      />
                      <span className="font-medium text-gray-900">{mode.title}</span>
                    </div>
                    <p className="text-sm text-gray-500 ml-6">{mode.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Smart Guidance Settings */}
          {formState.smart_paywall_enabled && (
            <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
              {/* Journey preview */}
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-xs font-medium text-gray-500 mb-3">{t('subscriptionPage.settings.journeyTitle')}</p>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="px-2 py-1 rounded-full text-xs bg-gray-200 text-gray-700">
                    {t('subscriptionPage.settings.journeyStart')}
                  </span>
                  {journeySteps.map((step, i) => (
                    <span key={i} className="flex items-center gap-2">
                      <span className="text-gray-400">→</span>
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${
                          step.soft ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {step.label.replace('{n}', String(step.value))}
                      </span>
                    </span>
                  ))}
                </div>
              </div>

              {hardBelowGuide && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-700">{t('subscriptionPage.settings.hardBelowGuideWarning')}</p>
                </div>
              )}

              {/* Step 1: soft guidance */}
              <div>
                <h3 className="text-base font-semibold text-gray-900">{t('subscriptionPage.settings.softSection')}</h3>
                <p className="text-sm text-gray-500 mt-1 mb-4">{t('subscriptionPage.settings.softSectionDesc')}</p>
                <div className="grid grid-cols-2 gap-6">
                  <NumberField
                    label={t('subscriptionPage.settings.freeDeepDailyLabel')}
                    help={t('subscriptionPage.settings.freeDeepDailyHelp')}
                    value={formState.free_deep_daily}
                    min={1}
                    onChange={(v) => handleChange('free_deep_daily', v)}
                  />
                  <NumberField
                    label={t('subscriptionPage.settings.fallbackDailyMessagesLabel')}
                    help={t('subscriptionPage.settings.fallbackDailyMessagesHelp')}
                    value={formState.fallback_daily_messages}
                    onChange={(v) => handleChange('fallback_daily_messages', v)}
                  />
                </div>
              </div>

              {/* Step 2: hard limits */}
              <div className="pt-4 border-t border-gray-100">
                <h3 className="text-base font-semibold text-gray-900">{t('subscriptionPage.settings.hardSection')}</h3>
                <p className="text-sm text-gray-500 mt-1 mb-4">{t('subscriptionPage.settings.hardSectionDesc')}</p>
                <div className="grid grid-cols-2 gap-6">
                  <NumberField
                    label={t('subscriptionPage.settings.smartPaywallHardMessagesDaily')}
                    help={t('subscriptionPage.settings.hardMessagesHelp')}
                    value={formState.hard_messages_daily}
                    onChange={(v) => handleChange('hard_messages_daily', v)}
                  />
                  <NumberField
                    label={t('subscriptionPage.settings.smartPaywallHardDeepDaily')}
                    help={t('subscriptionPage.settings.hardDeepHelp')}
                    value={formState.hard_deep_daily}
                    onChange={(v) => handleChange('hard_deep_daily', v)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Legacy Quota Settings */}
          {!formState.smart_paywall_enabled && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('subscriptionPage.settings.quotaSettings')}</h2>
              <p className="text-sm text-gray-500 mb-4">{t('subscriptionPage.settings.quotaUnlimitedHint')}</p>
              <div className="grid grid-cols-2 gap-6">
                <NumberField
                  label={t('subscriptionPage.settings.quotaTotalMessages')}
                  value={formState.quota_free_messages_total}
                  onChange={(v) => handleChange('quota_free_messages_total', v)}
                />
                <NumberField
                  label={t('subscriptionPage.settings.quotaDailyMessages')}
                  value={formState.quota_free_messages_daily}
                  onChange={(v) => handleChange('quota_free_messages_daily', v)}
                />
              </div>
            </div>
          )}

          {/* General */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('subscriptionPage.settings.generalSection')}</h2>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formState.paywall_show_on_launch}
                onChange={(e) => handleChange('paywall_show_on_launch', e.target.checked)}
                className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
              />
              <span className="text-gray-700">{t('subscriptionPage.settings.showOnLaunch')}</span>
            </label>
          </div>

          {/* Advanced: feature gating lists (collapsed by default) */}
          <div className="bg-white rounded-lg border border-gray-200">
            <button
              type="button"
              onClick={() => setAdvancedOpen(!advancedOpen)}
              className="w-full flex items-center gap-2 p-6 text-left"
            >
              {advancedOpen ? (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-400" />
              )}
              <span className="text-lg font-semibold text-gray-900">{t('subscriptionPage.settings.advancedSection')}</span>
            </button>
            {advancedOpen && (
              <div className="px-6 pb-6">
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                  <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-700">{t('subscriptionPage.settings.advancedSectionHint')}</p>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('subscriptionPage.settings.freeFeatures')}
                    </label>
                    <input
                      type="text"
                      value={formState.features_free}
                      onChange={(e) => handleChange('features_free', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      placeholder="basic_chat, daily_horoscope"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('subscriptionPage.settings.proFeatures')}
                    </label>
                    <input
                      type="text"
                      value={formState.features_pro}
                      onChange={(e) => handleChange('features_pro', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      placeholder="unlimited_chat, birth_chart, soulmate"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Reset Button */}
          {hasChanges && (
            <div className="flex justify-end">
              <Button variant="outline" onClick={handleReset}>
                {t('subscriptionPage.settings.resetChanges')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
